import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import RightPanel from '../../../components/RightPanel';
import { projectRequestsApi, customersApi, customerContactsApi } from '../../../services/api';
import { ProjectRequestDetail, ProjectRequestOptions, Customer, CustomerContactListItem } from '../types';
import { ExternalLink, Edit2, X, Save } from 'lucide-react';
import { showSuccess, showError } from '../../../utils/toast';
import Skeleton from '../../../components/Skeleton';
import Badge from '../../../components/ui/Badge';
import { projectRequestStatusVariant } from '../../../utils/badgeVariants';

type LightboxType = 'image' | 'pdf' | 'video' | 'doc';

interface ProjectRequestSidePanelProps {
  requestId: number;
  onClose: () => void;
  onUpdate?: () => void;
  initialEditMode?: boolean;
}

const ProjectRequestSidePanel: React.FC<ProjectRequestSidePanelProps> = ({ requestId, onClose, onUpdate, initialEditMode = false }) => {
  const [request, setRequest] = useState<ProjectRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(initialEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [form, setForm] = useState({
    customer_id: undefined as number | undefined,
    customer_name: '' as string,
    project_name: '' as string,
    funding_type: 'On-Call' as string,
    work_type: '' as string,
    work_request: [] as string[],
    impacted_parcels: '' as string,
    research_is_government_owned: 'No' as string,
    acquisition_is_government_owned: 'No' as string,
    qa_has_documentation: 'No' as string,
    comments_notes: '' as string,
  });
  
  // Track file removal
  const [requestEmailFileRemoved, setRequestEmailFileRemoved] = useState(false);
  const [supportingDocsFileRemoved, setSupportingDocsFileRemoved] = useState(false);

  // Options and customers
  const [options, setOptions] = useState<ProjectRequestOptions | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerContacts, setCustomerContacts] = useState<CustomerContactListItem[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  
  // Form errors
  const [clientError, setClientError] = useState<string | null>(null);
  const [workTypeError, setWorkTypeError] = useState<string | null>(null);
  const [workRequestError, setWorkRequestError] = useState<string | null>(null);

  // File states (for display only, not actual file uploads in edit mode)
  const [requestEmailFile, setRequestEmailFile] = useState<File | null>(null);
  const [supportingDocsFile, setSupportingDocsFile] = useState<File | null>(null);
  
  // Lightbox state
  const [lightbox, setLightbox] = useState<{ url: string; type: LightboxType } | null>(null);

  // Reset edit mode when requestId or initialEditMode changes
  useEffect(() => {
    setEditMode(initialEditMode);
  }, [requestId, initialEditMode]);

  // Load request data
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await projectRequestsApi.show(requestId);
        setRequest(data);
        
        // Reset file states when loading new request
        setRequestEmailFile(null);
        setSupportingDocsFile(null);
        setRequestEmailFileRemoved(false);
        setSupportingDocsFileRemoved(false);
        
        // Initialize form with request data
        setForm({
          customer_id: data.customer_id ?? undefined,
          customer_name: data.customer?.item || data.customer_name || '',
          project_name: data.project_name || '',
          funding_type: data.funding_type || 'On-Call',
          work_type: data.work_type || '',
          work_request: data.work_request || [],
          impacted_parcels: data.impacted_parcels !== null && data.impacted_parcels !== undefined ? String(data.impacted_parcels) : '',
          research_is_government_owned: data.research_is_government_owned === true ? 'Yes' : data.research_is_government_owned === false ? 'No' : 'No',
          acquisition_is_government_owned: data.acquisition_is_government_owned === true ? 'Yes' : data.acquisition_is_government_owned === false ? 'No' : 'No',
          qa_has_documentation: data.qa_has_documentation === true ? 'Yes' : data.qa_has_documentation === false ? 'No' : 'No',
          comments_notes: data.comments_notes || '',
        });
        setClientSearch(data.customer?.item || data.customer_name || '');
      } catch (error) {
        console.error('Failed to load project request:', error);
        setError('Failed to load project request');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [requestId]);

  // Load customers and contacts
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [cust, contacts] = await Promise.all([
          customersApi.getAll({ status: 'active', per_page: 1000 }),
          customerContactsApi.getAll(),
        ]);
        if (!active) return;
        setCustomers(cust.data);
        setCustomerContacts(contacts.data);
      } catch (e: any) {
        console.error('Failed to load customers:', e);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Load options when customer_id changes
  useEffect(() => {
    if (!editMode) return;
    let active = true;
    (async () => {
      try {
        const opts = await projectRequestsApi.options(form.customer_id);
        if (!active) return;
        setOptions(opts);
      } catch (e: any) {
        console.error('Failed to load options:', e);
      }
    })();
    return () => { active = false; };
  }, [form.customer_id, editMode]);

  const requiresResearch = useMemo(() => form.work_request.includes('Research'), [form.work_request]);
  const requiresAcquisition = useMemo(() => form.work_request.includes('Acquisition'), [form.work_request]);
  const requiresQaqc = useMemo(() => form.work_request.includes('QA/QC'), [form.work_request]);
  const requiresBasic = useMemo(() => form.work_request.includes('Basic Request'), [form.work_request]);

  const canSubmit = useMemo(() => {
    // Customer is read-only in edit mode, so check request.customer_id if form doesn't have it
    if (!form.customer_id && !request?.customer_id && !form.customer_name && !request?.customer_name) return false;
    if (!form.project_name) return false;
    if (!form.work_type) return false;
    if (form.work_request.length === 0) return false;
    if (requiresResearch && (form.impacted_parcels === '' || Number(form.impacted_parcels) < 0)) return false;
    if (requiresBasic && !requestEmailFile && !(request && request.request_email_file_path && !requestEmailFileRemoved)) return false;
    return true;
  }, [form, request, requiresResearch, requiresBasic, requestEmailFile, requestEmailFileRemoved]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as any;
    // Prevent negative values for impacted_parcels
    if (name === 'impacted_parcels' && type === 'number') {
      const numValue = Number(value);
      if (value !== '' && numValue < 0) {
        return;
      }
    }
    // Clear errors when user changes values
    if (name === 'work_type' && workTypeError) {
      setWorkTypeError(null);
    }
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRadioChange = (name: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleWorkRequestToggle = (value: string) => {
    setForm((prev) => {
      const exists = prev.work_request.includes(value);
      const next = exists ? prev.work_request.filter(v => v !== value) : [...prev.work_request, value];
      return { ...prev, work_request: next };
    });
    if (workRequestError) {
      setWorkRequestError(null);
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!clientSearch) return customers;
    return customers.filter(c => 
      c.item.toLowerCase().includes(clientSearch.toLowerCase())
    );
  }, [customers, clientSearch]);

  const getCustomerContacts = (customerId: number) => {
    return customerContacts.filter(contact => contact.customer_id === customerId);
  };

  const handleClientSelect = (customer: Customer) => {
    setForm((p) => ({ 
      ...p, 
      customer_id: customer.id,
      funding_type: customer.funding_type || 'On-Call' // Auto-select funding type from customer
    }));
    setClientSearch(customer.item);
    setShowClientDropdown(false);
    setClientError(null);
  };

  const handleClientSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientSearch(e.target.value);
    setShowClientDropdown(true);
    if (!e.target.value) {
      setForm((p) => ({ ...p, customer_id: undefined, customer_name: '' }));
    }
    if (clientError) {
      setClientError(null);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, fieldName: 'request_email_file' | 'supporting_docs_file') => {
    const file = e.target.files?.[0];
    if (file) {
      if (fieldName === 'request_email_file') {
        setRequestEmailFile(file);
        setRequestEmailFileRemoved(false);
      } else {
        setSupportingDocsFile(file);
        setSupportingDocsFileRemoved(false);
      }
    }
  };

  const handleRemoveFile = (fieldName: 'request_email_file' | 'supporting_docs_file') => {
    if (fieldName === 'request_email_file') {
      setRequestEmailFile(null);
      setRequestEmailFileRemoved(true);
    } else {
      setSupportingDocsFile(null);
      setSupportingDocsFileRemoved(true);
    }
  };

  // Helper to detect file type from URL
  const getFileTypeFromUrl = (url: string): LightboxType => {
    const lowerUrl = url.toLowerCase();
    if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(lowerUrl)) return 'image';
    if (/\.pdf$/i.test(lowerUrl)) return 'pdf';
    if (/\.(mp4|webm|ogg|mov|avi)$/i.test(lowerUrl)) return 'video';
    return 'doc';
  };

  // Open lightbox
  const openLightbox = (url: string, type?: LightboxType) => {
    setLightbox({ url, type: type || getFileTypeFromUrl(url) });
  };

  // Render file preview for File objects (from file input)
  const renderFilePreview = (file: File, fileUrl: string) => {
    const fileType = file.type;
    
    if (fileType.startsWith('image/')) {
      return (
        <img 
          src={fileUrl} 
          alt={file.name} 
          className="max-w-full h-auto max-h-64 rounded cursor-pointer hover:opacity-90 transition-opacity" 
          onClick={() => openLightbox(fileUrl, 'image')}
        />
      );
    } else if (fileType === 'application/pdf') {
      return (
        <div>
          <iframe src={fileUrl} className="w-full h-64 rounded border" title={file.name} />
          <button
            type="button"
            onClick={() => openLightbox(fileUrl, 'pdf')}
            className="mt-2 px-2 py-1 text-xs rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity theme-text-primary"
          >
            Open in Lightbox
          </button>
        </div>
      );
    } else if (fileType.startsWith('video/')) {
      return (
        <div>
          <video src={fileUrl} controls className="max-w-full h-auto max-h-64 rounded" />
          <button
            type="button"
            onClick={() => openLightbox(fileUrl, 'video')}
            className="mt-2 px-2 py-1 text-xs rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity theme-text-primary"
          >
            Open in Lightbox
          </button>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2 p-3 bg-gray-100 rounded">
          <span className="text-2xl">📄</span>
          <div>
            <div className="font-medium theme-text-primary">{file.name}</div>
            <div className="text-sm theme-text-secondary">{(file.size / 1024).toFixed(2)} KB</div>
          </div>
        </div>
      );
    }
  };

  // Render file preview for URLs (from database)
  const renderUrlPreview = (url: string, fileName: string, label: string) => {
    const fileType = getFileTypeFromUrl(url);
    const displayName = fileName || url.split('/').pop() || label;
    
    if (fileType === 'image') {
      return (
        <div className="mt-2">
            <img 
            src={url} 
            alt={displayName} 
            className="max-w-full h-auto max-h-64 rounded cursor-pointer hover:opacity-90 transition-opacity theme-border" 
            onClick={() => openLightbox(url, 'image')}
          />
          <p className="text-xs theme-text-tertiary mt-1">Click image to view in lightbox</p>
        </div>
      );
    } else if (fileType === 'pdf') {
      return (
        <div className="mt-2">
          <iframe src={url} className="w-full h-64 rounded theme-border" title={displayName} />
          <button
            type="button"
            onClick={() => openLightbox(url, 'pdf')}
            className="mt-2 px-2 py-1 text-xs rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity theme-text-primary"
          >
            Open in Lightbox
          </button>
        </div>
      );
    } else if (fileType === 'video') {
      return (
        <div className="mt-2">
          <video src={url} controls className="max-w-full h-auto max-h-64 rounded theme-border" />
          <button
            type="button"
            onClick={() => openLightbox(url, 'video')}
            className="mt-2 px-2 py-1 text-xs rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity theme-text-primary"
          >
            Open in Lightbox
          </button>
        </div>
      );
    } else {
      // For unknown file types, show a preview card with lightbox option
      return (
        <div className="mt-2">
          <div className="flex items-center gap-2 p-3 theme-bg-secondary rounded theme-border cursor-pointer hover:opacity-80 transition-opacity" onClick={() => openLightbox(url, 'doc')}>
            <span className="text-2xl">📄</span>
            <div className="flex-1">
              <div className="font-medium text-sm theme-text-primary">{displayName}</div>
              <p className="text-xs theme-text-tertiary mt-0.5">Click to preview in lightbox</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => openLightbox(url, 'doc')}
            className="mt-2 px-2 py-1 text-xs rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity theme-text-primary"
          >
            Open in Lightbox
          </button>
        </div>
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    let hasError = false;
    
    // Customer validation - ensure customer_id exists (should always be set if customer was selected)
    if (!form.customer_id && !request?.customer_id) {
      setClientError('Customer is required');
      hasError = true;
    }
    
    if (!form.work_type) {
      setWorkTypeError('Please fill this field');
      hasError = true;
    }
    
    if (form.work_request.length === 0) {
      setWorkRequestError('Please select at least one option');
      hasError = true;
    }
    
    if (hasError) return;
    
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    setClientError(null);
    setWorkTypeError(null);
    setWorkRequestError(null);
    
    try {
      // Use existing customer_id from request, don't allow changes
      const response = await projectRequestsApi.update(requestId, {
        customer_id: request?.customer_id ?? form.customer_id ?? null,
        customer_name: request?.customer?.item || request?.customer_name || form.customer_name || null,
        project_name: form.project_name,
        funding_type: form.funding_type || null,
        work_type: form.work_type || null,
        work_request: form.work_request,
        impacted_parcels: form.impacted_parcels === '' ? null : Number(form.impacted_parcels),
        research_is_government_owned: requiresResearch ? (form.research_is_government_owned === 'Yes') : null,
        acquisition_is_government_owned: requiresAcquisition ? (form.acquisition_is_government_owned === 'Yes') : null,
        qa_has_documentation: requiresQaqc ? (form.qa_has_documentation === 'Yes') : null,
        request_email_file: requiresBasic ? requestEmailFile || undefined : undefined,
        supporting_docs_file: supportingDocsFile || undefined,
        request_email_file_removed: requestEmailFileRemoved,
        supporting_docs_file_removed: supportingDocsFileRemoved,
        comments_notes: form.comments_notes || null,
      });

      // Reload request data
      const updatedData = await projectRequestsApi.show(requestId);
      setRequest(updatedData);
      
      // Reset file states
      setRequestEmailFile(null);
      setSupportingDocsFile(null);
      setRequestEmailFileRemoved(false);
      setSupportingDocsFileRemoved(false);
      
      // Show success toast
      showSuccess('Project request updated successfully!');
      
      // Switch back to view mode
      setEditMode(false);
      
      // Refresh list if callback provided
      if (onUpdate) {
        onUpdate();
      }
    } catch (e: any) {
      const errorMessage = e?.response?.data?.message || e?.message || 'Update failed';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form to original request data
    if (request) {
      setForm({
        customer_id: request.customer_id ?? undefined,
        customer_name: request.customer?.item || request.customer_name || '',
        project_name: request.project_name || '',
        funding_type: request.funding_type || 'On-Call',
        work_type: request.work_type || '',
        work_request: request.work_request || [],
        impacted_parcels: request.impacted_parcels !== null && request.impacted_parcels !== undefined ? String(request.impacted_parcels) : '',
        research_is_government_owned: request.research_is_government_owned === true ? 'Yes' : request.research_is_government_owned === false ? 'No' : 'No',
        acquisition_is_government_owned: request.acquisition_is_government_owned === true ? 'Yes' : request.acquisition_is_government_owned === false ? 'No' : 'No',
        qa_has_documentation: request.qa_has_documentation === true ? 'Yes' : request.qa_has_documentation === false ? 'No' : 'No',
        request_email_file_url: request.request_email_file_url || '',
        supporting_docs_url: request.supporting_docs_url || '',
        comments_notes: request.comments_notes || '',
      });
      setClientSearch(request.customer?.item || request.customer_name || '');
    }
    setEditMode(false);
    setError(null);
    setClientError(null);
    setWorkTypeError(null);
    setWorkRequestError(null);
  };

  const getStatusLabel = (status: string, hasDeal: boolean) => {
    if (!hasDeal) return 'PENDING';
    return status.replace('_', ' ').toUpperCase();
  };

  const header = (
    <div className="min-w-0">
      <p className="text-xs theme-text-secondary font-mono truncate">
        REQUEST-{requestId}
      </p>
      <h3 className="text-base font-semibold theme-text-primary truncate">
        {request?.project_name || 'Loading…'}
      </h3>
    </div>
  );

  const headerExtras = request && !request.deal && (
    <div className="flex items-center gap-2">
      {!editMode ? (
        <button
          onClick={() => setEditMode(true)}
          className="inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity theme-text-primary"
          title="Edit project request"
        >
          <Edit2 className="w-3.5 h-3.5" />
          Edit
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={handleCancel}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded theme-border theme-bg-secondary hover:opacity-80 transition-opacity disabled:opacity-50 theme-text-primary"
            title="Cancel editing"
          >
            <X className="w-3.5 h-3.5" />
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
            title="Save changes"
          >
            <Save className="w-3.5 h-3.5" />
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <RightPanel isOpen={true} onClose={onClose} title={header} headerExtras={headerExtras}>
      {loading ? (
        <div className="space-y-3 p-1">
          <Skeleton height={60} variant="rounded" />
          <div className="grid grid-cols-2 gap-3">
            <Skeleton height={48} variant="rounded" />
            <Skeleton height={48} variant="rounded" />
          </div>
          <Skeleton height={48} variant="rounded" />
          <Skeleton height={48} variant="rounded" />
          <Skeleton height={48} variant="rounded" />
          <Skeleton height={48} variant="rounded" />
        </div>
      ) : request ? (
        <div className="space-y-4">
          {error && (
            <div className="p-2 rounded bg-red-50 text-red-700 border border-red-200 text-sm">
              {error}
            </div>
          )}

          {editMode ? (
            // Edit Form
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Customer - Read Only */}
                <div>
                  <label className="block text-sm font-medium theme-text-primary mb-0.5">Customer</label>
                  <div className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-secondary theme-text-secondary">
                    {request.customer ? `${request.customer.item} (${request.customer.client_id})` : (request.customer_name || 'N/A')}
                  </div>
                  <p className="text-xs theme-text-tertiary mt-0.5">Customer cannot be changed</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-0.5">Project Name <span className='text-red-500'>*</span></label>
                  <input name="project_name" value={form.project_name} onChange={handleChange} className="w-full border rounded px-2 py-1 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-0.5">Funding Type</label>
                  <select name="funding_type" value={form.funding_type} onChange={handleChange} className="w-full border rounded px-2 py-1 text-sm">
                    <option value="On-Call">On-Call</option>
                    <option value="Project Management">Project Management</option>
                    {options?.funding_types.filter(o => o !== 'On-Call').map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-0.5">Work Type <span className='text-red-500'>*</span></label>
                  <select name="work_type" value={form.work_type} onChange={handleChange} className={`w-full border rounded px-2 py-1 text-sm ${workTypeError ? 'border-red-500' : ''}`}>
                    <option value="">Select</option>
                    {options?.work_types.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  {workTypeError && (
                    <p className="text-red-500 text-sm mt-0.5">{workTypeError}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">Work Request <span className='text-red-500'>*</span></label>
                <div className="flex flex-wrap gap-1.5">
                  {options?.work_request_options.map((o) => (
                    <button
                      key={o}
                      type="button"
                      onClick={() => handleWorkRequestToggle(o)}
                      className={`px-2 py-0.5 rounded-full border text-sm ${form.work_request.includes(o) ? 'bg-blue-600 text-white border-blue-600' : 'theme-bg-secondary theme-text-primary theme-border'} ${workRequestError ? 'border-red-500' : ''}`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
                {workRequestError && (
                  <p className="text-red-500 text-sm mt-0.5">{workRequestError}</p>
                )}
              </div>

              {(requiresResearch || requiresAcquisition || requiresQaqc) && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {requiresResearch && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-0.5">Impacted Parcels <span className='text-red-500'>*</span></label>
                        <input name="impacted_parcels" value={form.impacted_parcels} onChange={handleChange} type="number" min="0" className="w-full border rounded px-2 py-1 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-0.5">Research: Is Government Owned?</label>
                        <div className="flex gap-3">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="radio"
                              name="research_is_government_owned"
                              value="Yes"
                              checked={form.research_is_government_owned === 'Yes'}
                              onChange={(e) => handleRadioChange('research_is_government_owned', e.target.value)}
                              className="w-3.5 h-3.5"
                            />
                            <span className="text-sm">Yes</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="radio"
                              name="research_is_government_owned"
                              value="No"
                              checked={form.research_is_government_owned === 'No'}
                              onChange={(e) => handleRadioChange('research_is_government_owned', e.target.value)}
                              className="w-3.5 h-3.5"
                            />
                            <span className="text-sm">No</span>
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                  {requiresAcquisition && (
                    <div>
                      <label className="block text-sm font-medium mb-0.5">Acquisition: Is Government Owned?</label>
                      <div className="flex gap-3">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="acquisition_is_government_owned"
                            value="Yes"
                            checked={form.acquisition_is_government_owned === 'Yes'}
                            onChange={(e) => handleRadioChange('acquisition_is_government_owned', e.target.value)}
                            className="w-3.5 h-3.5"
                          />
                          <span className="text-sm">Yes</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="acquisition_is_government_owned"
                            value="No"
                            checked={form.acquisition_is_government_owned === 'No'}
                            onChange={(e) => handleRadioChange('acquisition_is_government_owned', e.target.value)}
                            className="w-3.5 h-3.5"
                          />
                          <span className="text-sm">No</span>
                        </label>
                      </div>
                    </div>
                  )}
                  {requiresQaqc && (
                    <div>
                      <label className="block text-sm font-medium mb-0.5">QA/QC: Do you have the necessary documentation?</label>
                      <div className="flex gap-3">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="qa_has_documentation"
                            value="Yes"
                            checked={form.qa_has_documentation === 'Yes'}
                            onChange={(e) => handleRadioChange('qa_has_documentation', e.target.value)}
                            className="w-3.5 h-3.5"
                          />
                          <span className="text-sm">Yes</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="radio"
                            name="qa_has_documentation"
                            value="No"
                            checked={form.qa_has_documentation === 'No'}
                            onChange={(e) => handleRadioChange('qa_has_documentation', e.target.value)}
                            className="w-3.5 h-3.5"
                          />
                          <span className="text-sm">No</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-0.5">Request Email File</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileUpload(e, 'request_email_file')}
                    className="w-full border rounded px-2 py-1 text-sm"
                  />
                  {(requestEmailFile || (request && request.request_email_file_url && !requestEmailFileRemoved)) && (
                    <div className="mt-2 border rounded p-2 bg-gray-50">
                      <div className="flex justify-between items-start mb-1.5">
                        <span className="text-sm font-medium">
                          {requestEmailFile ? requestEmailFile.name : (request?.request_email_file_name || 'Request Email File')}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile('request_email_file')}
                          className="text-red-600 hover:text-red-800 font-bold text-sm"
                        >
                          ✕
                        </button>
                      </div>
                      {requestEmailFile ? (
                        renderFilePreview(requestEmailFile, URL.createObjectURL(requestEmailFile))
                      ) : request?.request_email_file_url ? (
                        renderUrlPreview(request.request_email_file_url, request.request_email_file_name || '', 'Request Email File')
                      ) : null}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-0.5">Supporting Docs</label>
                  <input
                    type="file"
                    onChange={(e) => handleFileUpload(e, 'supporting_docs_file')}
                    className="w-full border rounded px-2 py-1 text-sm"
                  />
                  {(supportingDocsFile || (request && request.supporting_docs_url && !supportingDocsFileRemoved)) && (
                    <div className="mt-2 border rounded p-2 bg-gray-50">
                      <div className="flex justify-between items-start mb-1.5">
                        <span className="text-sm font-medium">
                          {supportingDocsFile ? supportingDocsFile.name : (request?.supporting_docs_file_name || 'Supporting Docs')}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile('supporting_docs_file')}
                          className="text-red-600 hover:text-red-800 font-bold text-sm"
                        >
                          ✕
                        </button>
                      </div>
                      {supportingDocsFile ? (
                        renderFilePreview(supportingDocsFile, URL.createObjectURL(supportingDocsFile))
                      ) : request?.supporting_docs_url ? (
                        renderUrlPreview(request.supporting_docs_url, request.supporting_docs_file_name || '', 'Supporting Docs')
                      ) : null}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-0.5">Comments/Notes</label>
                <textarea name="comments_notes" value={form.comments_notes} onChange={handleChange} className="w-full border rounded px-2 py-1 text-sm min-h-[80px]" />
              </div>
            </form>
          ) : (
            // View Mode
            <>
              {/* Status */}
              <div className="rounded-xl theme-border theme-card p-3">
                <h4 className="text-xs font-semibold theme-text-secondary mb-2">Status</h4>
                <Badge variant={projectRequestStatusVariant(request.status, !!request.deal)}>
                  {getStatusLabel(request.status, !!request.deal)}
                </Badge>
                {request.deal && (
                  <p className="text-xs theme-text-tertiary mt-2">This request has been converted to a deal and cannot be edited.</p>
                )}
              </div>

              {/* Project Details */}
              <div className="rounded-xl theme-border theme-card p-3">
                <h4 className="text-sm font-semibold theme-text-primary mb-3">Project Details</h4>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs theme-text-secondary">Project Name</p>
                    <p className="text-sm theme-text-primary font-medium">{request.project_name}</p>
                  </div>
                  <div>
                    <p className="text-xs theme-text-secondary">Customer</p>
                    <p className="text-sm theme-text-primary">
                      {request.customer ? `${request.customer.item} (${request.customer.client_id})` : (request.customer_name || 'N/A')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs theme-text-secondary">Submitted By</p>
                    <p className="text-sm theme-text-primary">{request.submitter?.name || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Funding & Work Type */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-1">Funding Type</h4>
                  <p className="text-sm theme-text-primary">{request.funding_type || 'N/A'}</p>
                </div>
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-1">Work Type</h4>
                  <p className="text-sm theme-text-primary">{request.work_type || 'N/A'}</p>
                </div>
              </div>

              {/* Work Request */}
              {request.work_request && request.work_request.length > 0 && (
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-2">Work Request</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {request.work_request.map((item, idx) => (
                      <li key={idx} className="text-sm theme-text-primary">{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Additional Details */}
              {request.impacted_parcels !== null && (
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-1">Impacted Parcels </h4>
                  <p className="text-sm theme-text-primary">{request.impacted_parcels}</p>
                </div>
              )}

              {/* Government Ownership */}
              {(request.research_is_government_owned !== null || request.acquisition_is_government_owned !== null) && (
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-2">Government Ownership</h4>
                  <div className="space-y-1">
                    {request.research_is_government_owned !== null && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm theme-text-primary">Research</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${request.research_is_government_owned ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {request.research_is_government_owned ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                    {request.acquisition_is_government_owned !== null && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm theme-text-primary">Acquisition</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${request.acquisition_is_government_owned ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {request.acquisition_is_government_owned ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* QA Documentation */}
              {request.qa_has_documentation !== null && (
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-1">QA Documentation</h4>
                  <span className={`text-xs px-2 py-0.5 rounded ${request.qa_has_documentation ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {request.qa_has_documentation ? 'Available' : 'Not Available'}
                  </span>
                </div>
              )}

              {/* Documents */}
              {(request.request_email_file_url || request.supporting_docs_url) && (
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-2">Documents</h4>
                  <div className="space-y-4">
                    {request.request_email_file_url && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium theme-text-primary">Request Email File</span>
                          <a
                            href={request.request_email_file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Open in new tab
                          </a>
                        </div>
                        {renderUrlPreview(request.request_email_file_url || '', request.request_email_file_name || '', 'Request Email File')}
                      </div>
                    )}
                    {request.supporting_docs_url && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium theme-text-primary">Supporting Documents</span>
                          <a
                            href={request.supporting_docs_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Open in new tab
                          </a>
                        </div>
                        {renderUrlPreview(request.supporting_docs_url || '', request.supporting_docs_file_name || '', 'Supporting Documents')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Comments/Notes */}
              {request.comments_notes && (
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-2">Comments & Notes</h4>
                  <p className="text-sm theme-text-primary leading-relaxed whitespace-pre-wrap">{request.comments_notes}</p>
                </div>
              )}

              {/* Expected Derived Products */}
              {request.derived_products && request.derived_products.length > 0 && (
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-2">Expected Derived Products</h4>
                  <div className="space-y-2">
                    {request.derived_products.map((product, idx) => (
                      <div key={idx} className="theme-border rounded-lg p-2 theme-bg-secondary">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium theme-text-primary">{product.label}</span>
                          <span className="text-xs theme-text-tertiary">{product.source}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs theme-text-secondary">
                          <span>Code: {product.roleCode}</span>
                          <span>Qty: {product.quantity}</span>
                          <span>Unit: {product.unit}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deal Info */}
              {request.deal && (
                <div className="rounded-xl theme-border theme-card p-3">
                  <h4 className="text-xs font-semibold theme-text-secondary mb-2">Converted to Deal</h4>
                  <div className="space-y-1">
                    <p className="text-sm theme-text-primary font-medium">{request.deal.name}</p>
                    <p className="text-xs theme-text-secondary">Stage: {request.deal.stage}</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="rounded-xl theme-border theme-card p-3">
                <h4 className="text-xs font-semibold theme-text-secondary mb-2">Timeline</h4>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="theme-text-secondary">Created</span>
                    <span className="theme-text-primary">{new Date(request.created_at).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="theme-text-secondary">Updated</span>
                    <span className="theme-text-primary">{new Date(request.updated_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="text-sm theme-text-tertiary">Project request not found</div>
      )}

      {/* Lightbox */}
      {lightbox && createPortal(
        <div 
          className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center" 
          onClick={() => setLightbox(null)}
        >
          <button 
            aria-label="Close" 
            className="absolute top-4 right-4 p-2 rounded-md bg-white/80 hover:bg-white shadow z-10" 
            onClick={() => setLightbox(null)}
          >
            <X className="w-5 h-5 theme-text-primary" />
          </button>
          {lightbox.type === 'image' && (
            <img 
              src={lightbox.url} 
              alt="preview" 
              className="max-h-[90vh] max-w-[90vw] rounded-lg border border-white/20" 
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {lightbox.type === 'pdf' && (
            <iframe 
              src={lightbox.url} 
              title="PDF preview" 
              className="w-[90vw] h-[90vh] rounded-lg bg-white" 
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {lightbox.type === 'video' && (
            <video 
              src={lightbox.url} 
              controls 
              autoPlay 
              className="w-[90vw] max-h-[90vh] rounded-lg bg-black" 
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {lightbox.type === 'doc' && (
            <iframe 
              src={lightbox.url} 
              title="Document preview" 
              className="w-[90vw] h-[90vh] rounded-lg bg-white" 
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>,
        document.body
      )}
    </RightPanel>
  );
};

export default ProjectRequestSidePanel;
